/**
 * Rubric Scoring Interface Component
 *
 * Interactive interface for scoring evaluations using the Danielson Framework
 * Includes domain breakdown, component scoring, evidence linking, and AI insights
 */

import React, { useState, useEffect, useMemo } from 'react';

import {
  ChevronDown,
  ChevronRight,
  Play,
  FileText,
  Brain,
  CheckCircle,
  Clock,
  Star,
  MessageSquare,
} from 'lucide-react';

import { cn } from '../../utils';
import { Button } from '../common/Button';
import { Card, CardHeader, CardContent } from '../common/Card';

// Types for rubric scoring
interface PerformanceLevel {
  level: 'ineffective' | 'developing' | 'effective' | 'highly_effective';
  score: number;
  description: string;
  indicators: string[];
}

interface RubricComponent {
  id: string;
  code: string;
  name: string;
  description: string;
  weight: number;
  performance_levels: PerformanceLevel[];
  current_score?: number;
  current_level?: string;
  evidence_items?: EvidenceItem[];
  ai_analysis?: AIAnalysis;
  evaluator_notes?: string;
  confidence_score?: number;
}

interface RubricDomain {
  id: string;
  name: string;
  description: string;
  weight: number;
  order: number;
  components: RubricComponent[];
  current_score?: number;
  completion_percentage?: number;
}

interface EvidenceItem {
  id: string;
  type: 'video_segment' | 'observation' | 'document' | 'artifact';
  source: string;
  timestamp?: { start: number; end: number };
  description: string;
  relevance_score: number;
}

interface AIAnalysis {
  suggested_score: number;
  suggested_level: string;
  confidence: number;
  key_observations: string[];
  rationale: string;
  video_highlights?: Array<{
    timestamp: { start: number; end: number };
    description: string;
    significance: 'high' | 'medium' | 'low';
  }>;
  /** True when sourced from persisted AI component drafts (human review required). */
  is_draft?: boolean;
  draft_id?: string;
  model_name?: string;
  prompt_version?: string;
}

interface RubricScoringInterfaceProps {
  evaluationId: string;
  rubricData: RubricDomain[];
  isReadOnly?: boolean;
  onScoreUpdate?: (
    componentId: string,
    score: number,
    level: string,
    notes?: string
  ) => void;
  onEvidenceAdd?: (
    componentId: string,
    evidence: Partial<EvidenceItem>
  ) => void;
  className?: string;
}

export const RubricScoringInterface: React.FC<RubricScoringInterfaceProps> = ({
  evaluationId: _evaluationId,
  rubricData,
  isReadOnly = false,
  onScoreUpdate,
  onEvidenceAdd: _onEvidenceAdd,
  className,
}) => {
  // State management
  const [expandedDomains, setExpandedDomains] = useState<Set<string>>(
    new Set()
  );
  const [expandedComponents, setExpandedComponents] = useState<Set<string>>(
    new Set()
  );
  const [selectedComponent, setSelectedComponent] = useState<string | null>(
    null
  );
  const [showAIInsights, setShowAIInsights] = useState<boolean>(true);
  const [scoringMode, setScoringMode] = useState<'guided' | 'manual'>('guided');

  // Initialize expanded state for first domain
  useEffect(() => {
    if (rubricData && rubricData.length > 0 && rubricData[0]) {
      setExpandedDomains(new Set([rubricData[0].id]));
    }
  }, [rubricData]);

  // Calculate overall progress
  const overallProgress = useMemo(() => {
    if (!rubricData.length) return 0;

    const totalComponents = rubricData.reduce(
      (sum, domain) => sum + domain.components.length,
      0
    );
    const scoredComponents = rubricData.reduce(
      (sum, domain) =>
        sum +
        domain.components.filter(comp => comp.current_score !== undefined)
          .length,
      0
    );

    return totalComponents > 0 ? (scoredComponents / totalComponents) * 100 : 0;
  }, [rubricData]);

  // Get performance level color
  const getPerformanceLevelColor = (level: string) => {
    switch (level) {
      case 'highly_effective':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'effective':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'developing':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'ineffective':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  // Get score color
  const getScoreColor = (score: number) => {
    if (score >= 3.5) return 'text-green-600';
    if (score >= 2.5) return 'text-blue-600';
    if (score >= 1.5) return 'text-yellow-600';
    return 'text-red-600';
  };

  // Toggle domain expansion
  const toggleDomain = (domainId: string) => {
    const newExpanded = new Set(expandedDomains);
    if (newExpanded.has(domainId)) {
      newExpanded.delete(domainId);
    } else {
      newExpanded.add(domainId);
    }
    setExpandedDomains(newExpanded);
  };

  // Toggle component expansion
  const toggleComponent = (componentId: string) => {
    const newExpanded = new Set(expandedComponents);
    if (newExpanded.has(componentId)) {
      newExpanded.delete(componentId);
    } else {
      newExpanded.add(componentId);
    }
    setExpandedComponents(newExpanded);
  };

  // Handle score change
  const handleScoreChange = (
    componentId: string,
    level: string,
    score: number
  ) => {
    if (isReadOnly || !onScoreUpdate) return;
    onScoreUpdate(componentId, score, level);
  };

  // Format time for video timestamps
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Render progress indicator
  const renderProgressIndicator = () => (
    <Card className="mb-6">
      <CardContent className="p-2">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-lg font-semibold text-gray-900">
            Scoring Progress
          </h3>
          <span className="text-sm text-gray-600">
            {Math.round(overallProgress)}% Complete
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-3">
          <div
            className="bg-blue-600 h-3 rounded-full transition-all duration-300"
            style={{ width: `${overallProgress}%` }}
          />
        </div>
        <div className="flex justify-between text-xs text-gray-500 mt-2">
          <span>Incomplete</span>
          <span>In Progress</span>
          <span>Complete</span>
        </div>
      </CardContent>
    </Card>
  );

  // Render AI insights for a component
  const renderAIInsights = (component: RubricComponent) => {
    if (!component.ai_analysis || !showAIInsights) return null;

    const { ai_analysis } = component;
    const isDraft = ai_analysis.is_draft === true;

    return (
      <Card className="mt-2 border-purple-200 bg-purple-50">
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <Brain className="w-5 h-5 text-purple-600" />
            <h4 className="font-semibold text-purple-900">
              {isDraft ? 'AI Draft (for review)' : 'AI Analysis'}
            </h4>
            <span className="text-xs bg-purple-200 text-purple-800 px-2 py-1 rounded-full">
              {isDraft
                ? 'Advisory only'
                : `${Math.round(ai_analysis.confidence * 100)}% Confidence`}
            </span>
          </div>

          <div className="space-y-3">
            <div>
              <p className="text-sm font-medium text-purple-900 mb-1">
                Suggested Score:
              </p>
              <div className="flex items-center gap-2">
                <span
                  className={cn(
                    'px-3 py-1 rounded-full text-sm font-medium border',
                    getPerformanceLevelColor(ai_analysis.suggested_level)
                  )}
                >
                  {ai_analysis.suggested_level.replace('_', ' ').toUpperCase()}
                </span>
                <span
                  className={cn(
                    'font-bold',
                    getScoreColor(ai_analysis.suggested_score)
                  )}
                >
                  {ai_analysis.suggested_score.toFixed(1)}
                </span>
              </div>
            </div>

            {isDraft && ai_analysis.model_name && (
              <p className="text-xs text-purple-700">
                Model {ai_analysis.model_name}
                {ai_analysis.prompt_version
                  ? ` · prompt ${ai_analysis.prompt_version}`
                  : ''}
              </p>
            )}

            <div>
              <p className="text-sm font-medium text-purple-900 mb-2">
                {isDraft ? 'Evidence from transcript:' : 'Key Observations:'}
              </p>
              <ul className="text-sm text-purple-800 space-y-1">
                {ai_analysis.key_observations.map((observation, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />
                    {observation}
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <p className="text-sm font-medium text-purple-900 mb-1">
                Rationale:
              </p>
              <p className="text-sm text-purple-800">{ai_analysis.rationale}</p>
            </div>

            {ai_analysis.video_highlights &&
              ai_analysis.video_highlights.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-purple-900 mb-2">
                    Key Video Moments:
                  </p>
                  <div className="space-y-2">
                    {ai_analysis.video_highlights.map((highlight, index) => (
                      <div
                        key={index}
                        className="flex items-center gap-2 text-sm"
                      >
                        <Play className="w-4 h-4 text-purple-600" />
                        <span className="font-mono text-purple-700">
                          {formatTime(highlight.timestamp.start)} -{' '}
                          {formatTime(highlight.timestamp.end)}
                        </span>
                        <span className="text-purple-800">
                          {highlight.description}
                        </span>
                        <span
                          className={cn(
                            'px-2 py-1 rounded text-xs',
                            highlight.significance === 'high'
                              ? 'bg-red-100 text-red-800'
                              : highlight.significance === 'medium'
                                ? 'bg-yellow-100 text-yellow-800'
                                : 'bg-green-100 text-green-800'
                          )}
                        >
                          {highlight.significance}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

            {!isReadOnly && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() =>
                  handleScoreChange(
                    component.id,
                    ai_analysis.suggested_level,
                    ai_analysis.suggested_score
                  )
                }
                className="mt-3"
              >
                {isDraft ? 'Accept draft into score' : 'Accept AI Suggestion'}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  // Render evidence items
  const renderEvidence = (component: RubricComponent) => {
    if (!component.evidence_items || component.evidence_items.length === 0)
      return null;

    return (
      <div className="mt-4">
        <h4 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
          <FileText className="w-4 h-4" />
          Evidence ({component.evidence_items.length})
        </h4>
        <div className="space-y-2">
          {component.evidence_items.map(evidence => (
            <div key={evidence.id} className="bg-gray-50 p-3 rounded-lg border">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    {evidence.type === 'video_segment' && (
                      <Play className="w-4 h-4 text-blue-600" />
                    )}
                    {evidence.type === 'observation' && (
                      <MessageSquare className="w-4 h-4 text-green-600" />
                    )}
                    {evidence.type === 'document' && (
                      <FileText className="w-4 h-4 text-purple-600" />
                    )}
                    <span className="text-sm font-medium text-gray-900">
                      {evidence.source}
                    </span>
                    {evidence.timestamp && (
                      <span className="text-xs text-gray-500 font-mono">
                        {formatTime(evidence.timestamp.start)} -{' '}
                        {formatTime(evidence.timestamp.end)}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-700">
                    {evidence.description}
                  </p>
                </div>
                <div className="flex items-center gap-1 ml-2">
                  <div className="flex items-center">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        className={cn(
                          'w-3 h-3',
                          i < Math.round(evidence.relevance_score * 5)
                            ? 'text-yellow-400 fill-current'
                            : 'text-gray-300'
                        )}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Render performance level selector
  const renderPerformanceLevelSelector = (component: RubricComponent) => {
    if (isReadOnly) return null;

    return (
      <div className="mt-4">
        <h4 className="text-sm font-semibold text-gray-900 mb-3">
          Rate this Component:
        </h4>
        <div className="space-y-2">
          {component.performance_levels.map(level => (
            <button
              key={level.level}
              onClick={() =>
                handleScoreChange(component.id, level.level, level.score)
              }
              className={cn(
                'w-full text-left p-3 rounded-lg border-2 transition-colors',
                component.current_level === level.level
                  ? getPerformanceLevelColor(level.level)
                  : 'border-gray-200 hover:border-gray-300 bg-white'
              )}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium">
                  {level.level.replace('_', ' ').toUpperCase()} ({level.score})
                </span>
                {component.current_level === level.level && (
                  <CheckCircle className="w-5 h-5 text-green-600" />
                )}
              </div>
              <p className="text-sm text-gray-700">{level.description}</p>
              {level.indicators.length > 0 && (
                <ul className="text-xs text-gray-600 mt-2 ml-4">
                  {level.indicators.slice(0, 2).map((indicator, index) => (
                    <li key={index} className="list-disc">
                      {indicator}
                    </li>
                  ))}
                  {level.indicators.length > 2 && (
                    <li className="list-disc italic">
                      + {level.indicators.length - 2} more indicators
                    </li>
                  )}
                </ul>
              )}
            </button>
          ))}
        </div>
      </div>
    );
  };

  // Render component
  const renderComponent = (component: RubricComponent) => {
    const isExpanded = expandedComponents.has(component.id);
    const isSelected = selectedComponent === component.id;
    const hasScore = component.current_score !== undefined;

    return (
      <div
        key={component.id}
        className={cn(
          'border rounded-lg transition-all',
          isSelected
            ? 'border-blue-300 bg-blue-50'
            : 'border-gray-200 bg-white',
          hasScore ? 'border-l-4 border-l-green-500' : ''
        )}
      >
        <div
          className="p-4 cursor-pointer"
          role="button"
          tabIndex={0}
          onClick={() => {
            toggleComponent(component.id);
            setSelectedComponent(isSelected ? null : component.id);
          }}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              toggleComponent(component.id);
              setSelectedComponent(isSelected ? null : component.id);
            }
          }}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 flex-1">
              {isExpanded ? (
                <ChevronDown className="w-5 h-5 text-gray-400" />
              ) : (
                <ChevronRight className="w-5 h-5 text-gray-400" />
              )}
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-mono text-sm text-gray-600 bg-gray-100 px-2 py-1 rounded">
                    {component.code}
                  </span>
                  <h4 className="font-semibold text-gray-900">
                    {component.name}
                  </h4>
                </div>
                <p className="text-sm text-gray-600">{component.description}</p>
              </div>
            </div>
            <div className="flex flex-col items-end gap-2">
              {hasScore && (
                <div className="flex items-center justify-center gap-4">
                  <p
                    className={cn(
                      'text-sm font-bold mt-1',
                      getScoreColor(component.current_score || 0)
                    )}
                  >
                    {component.current_score?.toFixed(1)}
                  </p>
                  <div
                    className={cn(
                      'px-3 py-1 rounded-full text-sm font-medium border',
                      getPerformanceLevelColor(component.current_level || '')
                    )}
                  >
                    {component.current_level?.replace('_', ' ').toUpperCase()}
                  </div>
                </div>
              )}
              <div className="flex items-center gap-4">
                {component.confidence_score && (
                  <div className="text-xs text-gray-500">
                    {Math.round(component.confidence_score * 100)}%
                  </div>
                )}

                {component.ai_analysis && (
                  <Brain className="w-5 h-5 text-purple-500" />
                )}

                {!hasScore && (
                  <div className="flex items-center gap-1 text-gray-400">
                    <Clock className="w-4 h-4" />
                    <span className="text-sm">Not scored</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {isExpanded && (
          <div className="border-t border-gray-200 p-4 space-y-4">
            {renderPerformanceLevelSelector(component)}
            {renderAIInsights(component)}
            {renderEvidence(component)}

            {!isReadOnly && (
              <div>
                <label
                  htmlFor={`notes-${component.id}`}
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Evaluator Notes:
                </label>
                <textarea
                  id={`notes-${component.id}`}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Add notes about this component..."
                  defaultValue={component.evaluator_notes || ''}
                />
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // Render domain
  const renderDomain = (domain: RubricDomain) => {
    const isExpanded = expandedDomains.has(domain.id);
    const completedComponents = domain.components.filter(
      c => c.current_score !== undefined
    ).length;
    const totalComponents = domain.components.length;
    const completionPercentage =
      totalComponents > 0 ? (completedComponents / totalComponents) * 100 : 0;

    return (
      <Card key={domain.id} className="mb-6">
        <CardHeader
          className="!pb-0 !mb-0 cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors"
          onClick={() => toggleDomain(domain.id)}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 mx-2">
              {isExpanded ? (
                <ChevronDown className="w-6 h-6 text-gray-600" />
              ) : (
                <ChevronRight className="w-6 h-6 text-gray-600" />
              )}
              <div className="flex flex-col p-4">
                <h3 className="text-xl font-bold text-gray-900">
                  {domain.name}
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  {domain.description}
                </p>
              </div>
            </div>
            <div className="text-right gap-2 px-2">
              <div className="flex items-center justify-between gap-2 mb-1">
                <span className="text-sm text-gray-600">
                  {completedComponents}/{totalComponents} components
                </span>
                {domain.current_score && (
                  <span
                    className={cn(
                      'font-bold text-lg',
                      getScoreColor(domain.current_score)
                    )}
                  >
                    {domain.current_score.toFixed(1)}
                  </span>
                )}
              </div>
              <div className="w-36 bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${completionPercentage}%` }}
                />
              </div>
            </div>
          </div>
        </CardHeader>

        {isExpanded && (
          <CardContent className="p-4">
            <div className="space-y-4">
              {domain.components.map(renderComponent)}
            </div>
          </CardContent>
        )}
      </Card>
    );
  };

  return (
    <div className={cn('space-y-6', className)}>
      {/* Header Controls */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">
          Danielson Framework Scoring
        </h2>
        <div className="flex items-center gap-3">
          <Button
            variant={showAIInsights ? 'primary' : 'secondary'}
            size="md"
            onClick={() => setShowAIInsights(!showAIInsights)}
            className="flex items-center gap-2"
            icon={<Brain className="w-4 h-4" />}
          >
            AI Insights
          </Button>
          {!isReadOnly && (
            <select
              value={scoringMode}
              onChange={e =>
                setScoringMode(e.target.value as 'guided' | 'manual')
              }
              className="px-3 py-2 border border-gray-300 rounded-md text-sm"
            >
              <option value="guided">Guided Scoring</option>
              <option value="manual">Manual Scoring</option>
            </select>
          )}
        </div>
      </div>

      {/* Progress Indicator */}
      {renderProgressIndicator()}

      {/* Domains */}
      <div className="space-y-6">{rubricData.map(renderDomain)}</div>

      {/* Summary */}
      {overallProgress > 0 && (
        <Card>
          <CardContent className="p-2">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Evaluation Summary
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {rubricData.map(domain => {
                const completedComponents = domain.components.filter(
                  c => c.current_score !== undefined
                ).length;
                const totalComponents = domain.components.length;
                return (
                  <div key={domain.id} className="text-center">
                    <p className="text-sm font-medium text-gray-600">
                      {domain.name}
                    </p>
                    <p className="text-2xl font-bold text-gray-900 mt-1">
                      {domain.current_score?.toFixed(1) || '--'}
                    </p>
                    <p className="text-xs text-gray-500">
                      {completedComponents}/{totalComponents} scored
                    </p>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default RubricScoringInterface;
